import { useState, useEffect } from 'react'
import Modal from './Modal'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { apiService } from '@/service/api'
import type { DigitalCoworkerRecord } from '@/service/api'
import { toast } from 'sonner'
import { trackEvent } from '@/utils/analytics'
import { useTranslation } from '@/i18n'
import { Phone } from 'lucide-react'
import { coworkerAvatarUrl } from '@/utils/coworkerAvatar'

interface DigitalCoworkerCallModalProps {
  coworker: DigitalCoworkerRecord | null
  isOpen: boolean
  onClose: () => void
}

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(w => w.length > 0)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const DigitalCoworkerCallModal = ({ coworker, isOpen, onClose }: DigitalCoworkerCallModalProps) => {
  const { t } = useTranslation()
  const [meetingUrl, setMeetingUrl] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setMeetingUrl('')
    }
  }, [isOpen])

  const handleSubmit = async () => {
    if (!coworker) return

    if (!meetingUrl.trim()) {
      toast.error(t('chat:call_config.errors.no_meeting_link'))
      return
    }

    setIsLoading(true)
    try {
      const webapp_url = `${window.location.origin}/meeting`
      await apiService.startMeeting(
        meetingUrl,
        webapp_url,
        null,
        null,
        coworker.id
      )

      const url = meetingUrl.toLowerCase()
      const platform = url.includes('meet.google.com')
        ? 'google_meet'
        : url.includes('teams.microsoft.com') || url.includes('teams.live.com')
          ? 'ms_teams'
          : url.includes('zoom.us')
            ? 'zoom'
            : 'unknown'

      trackEvent('ai_call-started', {
        digital_coworker_id: coworker.id,
        coworker_name: coworker.name,
        meeting_platform: platform,
      })

      toast.success(t('digital_coworker:call.toasts.adding', { name: coworker.name }))
      setMeetingUrl('')
      onClose()
    } catch (error) {
      console.error('Error starting call:', error)
      const errorMessage = error instanceof Error ? error.message : ''
      trackEvent('ai_call-failed', {
        digital_coworker_id: coworker.id,
        error_type: errorMessage || 'unknown',
      })
      if (errorMessage === 'BOT_ALREADY_EXISTS') {
        toast.error(t('chat:call_config.errors.bot_already_exists'))
      } else {
        toast.error(t('chat:call_config.errors.failed_to_start'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!coworker) return null

  const avatarUrl = coworkerAvatarUrl(coworker)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('digital_coworker:call.title')}
      description={t('digital_coworker:call.description', { name: coworker.name })}
      className="max-w-[480px]"
      footer={
        <>
          <Button variant="outline" onClick={onClose} className="px-6">
            {t('chat:call_config.button_cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !meetingUrl.trim()}
            className="px-6 gap-2"
          >
            <Phone className="h-4 w-4" />
            {isLoading ? t('chat:call_config.button_starting') : t('digital_coworker:call.button_add')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5 py-2">
        {/* Coworker identity */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-muted rounded-xl">
          <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center font-semibold text-orange-600 dark:text-orange-400 text-sm overflow-hidden flex-shrink-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={coworker.name}
                className="w-full h-full object-cover"
              />
            ) : (
              getInitials(coworker.name)
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-dark-text">{coworker.name}</p>
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">
              {coworker.job_title}
              {coworker.department ? ` · ${coworker.department}` : ''}
            </p>
          </div>
        </div>

        {/* Meeting link input */}
        <div>
          <label className="text-sm font-semibold text-gray-900 dark:text-dark-text mb-2 block">
            {t('chat:call_config.meeting_link_label')}
          </label>
          <Input
            className="w-full bg-gray-100 dark:bg-dark-accent border-0 h-10"
            placeholder={t('chat:call_config.placeholder_meeting_url')}
            type="text"
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && meetingUrl.trim()) void handleSubmit()
            }}
          />
          <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1.5">
            {t('chat:call_config.meeting_platforms')}
          </p>
        </div>
      </div>
    </Modal>
  )
}

export default DigitalCoworkerCallModal
